import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, ReplaySubject, Subscription } from 'rxjs';
import { IntegrationModel, Platform } from 'src/app/models/api';
import {
  IdentityProvider,
  PolicyModel,
  PolicyToUpdateModel,
  PolicyType,
} from 'src/app/models/api/governance/policy.model';
import { StatusCodeEnum } from 'src/app/models/api/integration.model';
import { ISelectOption } from 'src/app/models/selectOptions';
import { IdentityProvidersSelectOptions } from 'src/app/models/selectOptions/identityProvidersSelectOptions';
import { PolicyTypeSelectOptions } from 'src/app/models/selectOptions/policyTypeSelectOptions.model';
import { PoliciesHttpService } from 'src/app/services/httpServices/governance/policiesHttpService/policies-http.service';
import { HttpErrorHandlerService } from 'src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service';
import { NotificationsService } from 'src/app/services/infra/notifications/notifications.service';
import { InactiveIntegrationDialogComponent } from '../inactive-integration-dialog/inactive-integration-dialog.component';
import { EffectivePolicySettingEnum } from 'src/app/services/infra/store/model/effectivePolicySetting.model';
import _ from 'lodash';

interface ISettingsFrom {
  policyName: string;
  policyType: string;
  numberOfBuilders: number;
  highNumberOfPrivilegedUsers: number;
  criticalNumberOfPrivilegedUsers: number;
  connectionsToProd: boolean;
  connectionsToNonProd: boolean;
  allowedIdentityProviders: IdentityProvider[];
  allowedNumOfPrivilegedIdentitiesInWorkspace: number;
  allowedNumOfPrivilegedIdentitiesToDashboard: number;
  allowedNumOfPrivilegedIdentitiesToDataset: number;
  allowedNumOfPrivilegedIdentitiesToReport: number;
  allowedNumOfPrivilegedIdentitiesToAgent: number;
  allowedNumOfPrivilegedIdentitiesToAiSkill: number;
  allowedNumOfPrivilegedIdentitiesToGpt: number;
  allowedNumOfPrivilegedIdentitiesToTable: number;
  allowedMemoryRetentionInDays: number;
  allowedMemoryMaxRecentSessions: number;
}
@Component({
  selector: 'app-general-settings',
  templateUrl: './general-settings.component.html',
  styleUrls: ['./general-settings.component.scss'],
})
export class GeneralSettingsComponent implements OnInit, OnDestroy {
  @Input() public policyModel: PolicyModel;
  @Input() public integrationModel: IntegrationModel;
  @Input() public editable = true;
  @Output() public goBack: EventEmitter<void> = new EventEmitter<void>();
  @Output() public policyUpdated: EventEmitter<PolicyModel> = new EventEmitter<PolicyModel>();
  @Output() public notifyChanges: EventEmitter<boolean> = new EventEmitter<boolean>();

  public settingsFormGroup: UntypedFormGroup;

  public EffectivePolicySettingEnum = EffectivePolicySettingEnum;

  // policy type
  public policyTypes: ISelectOption[] = PolicyTypeSelectOptions.filter((type) => type.value !== PolicyType.default);

  public policyTypeSelected?: ISelectOption;
  public changesWereDone = false;

  // identity providers
  public identityProvidersOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject(
    IdentityProvidersSelectOptions
  );
  public identityProvidersSelected: string[] = [];

  // loading behavior subject
  public isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public injectCategoryOptionIndex: ReplaySubject<number> = new ReplaySubject<number>();

  public platformEnum = Platform;
  public platformsWithoutConnectionsToProdConfiguration = [
    Platform.powerBI,
    Platform.salesforce,
    Platform.chatGptEnterprise,
    Platform.amazonBedrock,
    Platform.google,
    Platform.devices,
    Platform.cursor,
  ];

  private _startState: ISettingsFrom;

  // subscriptions
  private _policyConfigUpdateSubscription$?: Subscription;
  private _getPolicySubscription$?: Subscription;
  private _formValuesChanged$?: Subscription;

  constructor(
    private readonly _fb: UntypedFormBuilder,
    private readonly _matDialog: MatDialog,
    private readonly _policiesHttpService: PoliciesHttpService,
    private readonly _httpErrorHandlerService: HttpErrorHandlerService,
    private readonly _notificationService: NotificationsService
  ) {}

  ngOnDestroy() {
    this._policyConfigUpdateSubscription$?.unsubscribe();
    this._formValuesChanged$?.unsubscribe();
    this._getPolicySubscription$?.unsubscribe();
  }

  ngOnInit(): void {
    const policyType = this.policyModel.type;
    this.policyTypeSelected = PolicyTypeSelectOptions.find((type) => type.value === policyType);

    this.identityProvidersSelected = this.policyModel.config.allowedIdentityProviders;

    this.settingsFormGroup = this._fb.group({
      policyName: this._fb.control(this.policyModel.name, [Validators.required, Validators.minLength(1)]),
      policyType: this._fb.control(this.policyTypeSelected?.displayName, [Validators.required]),
      numberOfBuilders: this._fb.control(this.policyModel.config.allowedNumOfBuilders, [
        Validators.required,
        Validators.min(1),
      ]),
      highNumberOfPrivilegedUsers: this._fb.control(this.policyModel.config.numOfHighPrivilegedUsersInEnvironment, [
        Validators.required,
        Validators.min(1),
      ]),
      criticalNumberOfPrivilegedUsers: this._fb.control(
        this.policyModel.config.numOfCriticalPrivilegedUsersInEnvironment,
        [Validators.required, Validators.min(1)]
      ),
      connectionsToProd: this._fb.control(this.policyModel.config.allowConnectionsToProdEnvs, [Validators.required]),
      connectionsToNonProd: this._fb.control(this.policyModel.config.allowConnectionsToNonProdEnvs, [
        Validators.required,
      ]),
      allowedIdentityProviders: this._fb.control(this.policyModel.config.allowedIdentityProviders, [
        Validators.required,
      ]),
      allowedNumOfPrivilegedIdentitiesInWorkspace: this._fb.control(
        this.policyModel.config.allowedNumOfPrivilegedIdentitiesInWorkspace,
        [Validators.required, Validators.min(1)]
      ),
      allowedNumOfPrivilegedIdentitiesToDashboard: this._fb.control(
        this.policyModel.config.allowedNumOfPrivilegedIdentitiesToDashboard,
        [Validators.required, Validators.min(1)]
      ),
      allowedNumOfPrivilegedIdentitiesToDataset: this._fb.control(
        this.policyModel.config.allowedNumOfPrivilegedIdentitiesToDataset,
        [Validators.required, Validators.min(1)]
      ),
      allowedNumOfPrivilegedIdentitiesToReport: this._fb.control(
        this.policyModel.config.allowedNumOfPrivilegedIdentitiesToReport,
        [Validators.required, Validators.min(1)]
      ),
      allowedNumOfPrivilegedIdentitiesToAgent: this._fb.control(
        this.policyModel.config.allowedNumOfPrivilegedIdentitiesToAgent,
        [Validators.required, Validators.min(1)]
      ),
      allowedNumOfPrivilegedIdentitiesToAiSkill: this._fb.control(
        this.policyModel.config.allowedNumOfPrivilegedIdentitiesToAiSkill,
        [Validators.required, Validators.min(1)]
      ),
      allowedNumOfPrivilegedIdentitiesToGpt: this._fb.control(
        this.policyModel.config.allowedNumOfPrivilegedIdentitiesToGpt,
        [Validators.required, Validators.min(1)]
      ),
      allowedNumOfPrivilegedIdentitiesToTable: this._fb.control(
        this.policyModel.config.allowedNumOfPrivilegedIdentitiesToTable,
        [Validators.required, Validators.min(1)]
      ),
      allowedMemoryRetentionInDays: this._fb.control(this.policyModel.config.allowedMemoryRetentionInDays, [
        Validators.required,
        Validators.min(0),
      ]),
      allowedMemoryMaxRecentSessions: this._fb.control(this.policyModel.config.allowedMemoryMaxRecentSessions, [
        Validators.required,
        Validators.min(0),
      ]),
    });

    this._startState = this.settingsFormGroup.getRawValue() as ISettingsFrom;

    this._formValuesChanged$ = this.settingsFormGroup.valueChanges.subscribe((values) => {
      if (
        values['policyName'] == this._startState.policyName &&
        values['policyType'] == this._startState.policyType &&
        values['numberOfBuilders'] == this._startState.numberOfBuilders &&
        values['highNumberOfPrivilegedUsers'] == this._startState.highNumberOfPrivilegedUsers &&
        values['criticalNumberOfPrivilegedUsers'] == this._startState.criticalNumberOfPrivilegedUsers &&
        values['connectionsToProd'] == this._startState.connectionsToProd &&
        values['connectionsToNonProd'] == this._startState.connectionsToNonProd &&
        values['allowedNumOfPrivilegedIdentitiesInWorkspace'] ==
          this._startState.allowedNumOfPrivilegedIdentitiesInWorkspace &&
        values['allowedNumOfPrivilegedIdentitiesToDashboard'] ==
          this._startState.allowedNumOfPrivilegedIdentitiesToDashboard &&
        values['allowedNumOfPrivilegedIdentitiesToDataset'] ==
          this._startState.allowedNumOfPrivilegedIdentitiesToDataset &&
        values['allowedNumOfPrivilegedIdentitiesToReport'] ==
          this._startState.allowedNumOfPrivilegedIdentitiesToReport &&
        values['allowedNumOfPrivilegedIdentitiesToAgent'] == this._startState.allowedNumOfPrivilegedIdentitiesToAgent &&
        values['allowedNumOfPrivilegedIdentitiesToAiSkill'] ==
          this._startState.allowedNumOfPrivilegedIdentitiesToAiSkill &&
        values['allowedNumOfPrivilegedIdentitiesToGpt'] == this._startState.allowedNumOfPrivilegedIdentitiesToGpt &&
        values['allowedNumOfPrivilegedIdentitiesToTable'] == this._startState.allowedNumOfPrivilegedIdentitiesToTable &&
        values['allowedMemoryRetentionInDays'] == this._startState.allowedMemoryRetentionInDays &&
        values['allowedMemoryMaxRecentSessions'] == this._startState.allowedMemoryMaxRecentSessions &&
        JSON.stringify(values['allowedIdentityProviders']) == JSON.stringify(this._startState.allowedIdentityProviders)
      ) {
        this.changesWereDone = false;
      } else {
        this.changesWereDone = true;
      }

      this.notifyChanges.emit(this.changesWereDone);
    });
  }

  public onPolicyTypeSelection(selectedPolicyType: ISelectOption) {
    this.policyTypeSelected = this.policyTypes.find((type) => type.id === selectedPolicyType.id);
    this.settingsFormGroup.controls['policyType'].setValue(this.policyTypeSelected?.displayName);
  }

  public onIdentityProvidersSelection(selectedIdentityProviders: ISelectOption[]) {
    this.identityProvidersSelected = selectedIdentityProviders.map((option) => option.id);
    this.settingsFormGroup.controls['allowedIdentityProviders'].setValue(
      selectedIdentityProviders.map((option) => option.value)
    );
  }

  public updatePolicyConfig() {
    if (this.integrationModel.connectionStatus.code == StatusCodeEnum.off) {
      const dialogRef = this._matDialog.open(InactiveIntegrationDialogComponent, {
        data: this.integrationModel,
        disableClose: false,
        panelClass: 'dynamic-size-popup-dialog-container',
      });
      dialogRef.afterClosed().subscribe(() => {
        this._updatePolicyConfig();
      });
    } else {
      this._updatePolicyConfig();
    }
  }

  public resetForm() {
    const index = this.policyTypes.findIndex((type) => type.displayName === this._startState.policyType);
    this.injectCategoryOptionIndex.next(index);
    this.identityProvidersSelected = this._startState.allowedIdentityProviders;
    this.settingsFormGroup.reset(this._startState);
  }

  public cancelSettings() {
    this.goBack.emit();
  }

  private _updatePolicyConfig() {
    this.isLoading$.next(true);
    this._generateUpdatePolicyModel();
    this._policyConfigUpdateSubscription$ = this._policiesHttpService
      .updatePolicy(this.integrationModel.id, this.policyModel.id, this._generateUpdatePolicyModel())
      .subscribe(
        () => {
          this._notificationService.success('Policy updated successfully');
          this._getPolicySubscription$ = this._policiesHttpService
            .getPolicy(this.integrationModel.id, this.policyModel.id)
            .subscribe((policy) => {
              this.policyModel = policy;
              this._startState = this.settingsFormGroup.getRawValue() as ISettingsFrom;
              this.resetForm();
              this.isLoading$.next(false);
              this.policyUpdated.emit(this.policyModel);
            });
        },
        (error) => this._httpErrorHandlerService.handle(error)
      );
  }

  private _generateUpdatePolicyModel(): PolicyToUpdateModel {
    const policyType: PolicyType =
      PolicyType[this.settingsFormGroup.controls['policyType'].value.toLowerCase() as PolicyType];
    const config = _.cloneDeep(this.policyModel.config);
    config.allowedIdentityProviders = this.settingsFormGroup.controls['allowedIdentityProviders'].value;
    config.allowedNumOfBuilders = this.settingsFormGroup.controls['numberOfBuilders'].value;
    config.numOfHighPrivilegedUsersInEnvironment = this.settingsFormGroup.controls['highNumberOfPrivilegedUsers'].value;
    config.numOfCriticalPrivilegedUsersInEnvironment =
      this.settingsFormGroup.controls['criticalNumberOfPrivilegedUsers'].value;
    config.allowConnectionsToProdEnvs = this.settingsFormGroup.controls['connectionsToProd'].value;
    config.allowConnectionsToNonProdEnvs = this.settingsFormGroup.controls['connectionsToNonProd'].value;
    config.allowedNumOfPrivilegedIdentitiesInWorkspace =
      this.settingsFormGroup.controls['allowedNumOfPrivilegedIdentitiesInWorkspace'].value;
    config.allowedNumOfPrivilegedIdentitiesToDashboard =
      this.settingsFormGroup.controls['allowedNumOfPrivilegedIdentitiesToDashboard'].value;
    config.allowedNumOfPrivilegedIdentitiesToDataset =
      this.settingsFormGroup.controls['allowedNumOfPrivilegedIdentitiesToDataset'].value;
    config.allowedNumOfPrivilegedIdentitiesToReport =
      this.settingsFormGroup.controls['allowedNumOfPrivilegedIdentitiesToReport'].value;
    config.allowedNumOfPrivilegedIdentitiesToAgent =
      this.settingsFormGroup.controls['allowedNumOfPrivilegedIdentitiesToAgent'].value;
    config.allowedNumOfPrivilegedIdentitiesToGpt =
      this.settingsFormGroup.controls['allowedNumOfPrivilegedIdentitiesToGpt'].value;
    config.allowedNumOfPrivilegedIdentitiesToTable =
      this.settingsFormGroup.controls['allowedNumOfPrivilegedIdentitiesToTable'].value;
    config.allowedNumOfPrivilegedIdentitiesToAiSkill =
      this.settingsFormGroup.controls['allowedNumOfPrivilegedIdentitiesToAiSkill'].value;
    config.allowedMemoryRetentionInDays = this.settingsFormGroup.controls['allowedMemoryRetentionInDays'].value;
    config.allowedMemoryMaxRecentSessions = this.settingsFormGroup.controls['allowedMemoryMaxRecentSessions'].value;

    return {
      name: this.settingsFormGroup.controls['policyName'].value,
      description: this.policyModel.description,
      type: this.policyModel.isDefault ? undefined : policyType,
      config: config,
    } as PolicyToUpdateModel;
  }
}
